/**
 * Phase H — Live progress drawer for Discovery scan runs.
 *
 * <p>Mounted at the Discovery Hub level so it survives tab navigation.
 * When the user clicks "Run Discovery Scan" the parent calls
 * {@code shadowDiscoveryScanTriggerAsync()} and feeds the returned
 * {@code runId} into this component via the {@code runId} prop. The
 * drawer then polls {@code GET /scans/runs/{runId}} every 1.5 s until
 * the run reaches a terminal phase, rendering each engine as a row that
 * transitions queued → running → completed/failed with live counts.
 *
 * <p><b>Design</b>: right-side fixed panel ({@code w-[420px]}), full
 * height, top-shadow border. When minimised collapses to a small pill
 * at the bottom-right corner so the operator can keep working while
 * the scan finishes. Auto-collapses to the pill 5 s after a successful
 * run; stays open on failure so the operator can read the error.
 *
 * <p><b>Why polling not WebSocket</b>: the platform WS bus carries
 * scan-related events but they're broadcast (no per-runId filter) and
 * we'd have to plumb a subscription keyed by runId. Polling at 1.5 s
 * is cheap (one GET, ~1 KB payload, only while the drawer is open),
 * and the implementation is small enough to skim in a code review.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as api from '../../services/api';

// ── Style tokens (match Tailwind v2 design system used elsewhere) ──────
const PANEL_WIDTH = 420;

// Map engine type → display label. Falls back to the raw constant for
// any new engine we haven't added an alias for, so the drawer never
// renders {undefined}.
const ENGINE_LABEL = {
  NETWORK:        'Network intelligence',
  MCP_PROTOCOL:   'MCP protocol',
  CLOUD_SCAN:     'Cloud platform',
  CODE_REPO:      'Code repository',
  IDENTITY:       'Identity forensics',
  BEHAVIORAL:     'Behavioral fingerprint',
  CORRELATION:    'Cross-engine correlation',
};

/* DEF-DISCOVERY-DRAWER-ENGINE-ORDER-001 — PostgreSQL JSONB does NOT
 * preserve object-key insertion order across the write/read cycle, so
 * the engines Map comes back from the backend in an unpredictable
 * order (observed: NETWORK, IDENTITY, CODE_REPO, BEHAVIORAL, CLOUD_SCAN,
 * MCP_PROTOCOL — likely the JSONB internal key-length-then-lexical
 * ordering rule). The drawer enforces a canonical execution order so
 * the rows are stable regardless of what the JSONB layer returns. Any
 * unknown engine type lands at the end of the list. */
const ENGINE_ORDER = [
  'NETWORK', 'MCP_PROTOCOL', 'CLOUD_SCAN', 'CODE_REPO', 'IDENTITY', 'BEHAVIORAL', 'CORRELATION',
];
function sortEngineEntries(engines) {
  if (!engines) return [];
  const entries = Object.entries(engines);
  const known = new Set(ENGINE_ORDER);
  const knownSorted = ENGINE_ORDER
    .filter((key) => key in engines)
    .map((key) => [key, engines[key]]);
  const unknownTail = entries.filter(([key]) => !known.has(key));
  return [...knownSorted, ...unknownTail];
}

const PHASE_LABEL = {
  QUEUED:      'Queued',
  SCANNING:    'Scanning engines',
  CORRELATING: 'Correlating signals',
  FINALIZING:  'Applying policies',
  COMPLETED:   'Completed',
  FAILED:      'Failed',
  CANCELED:    'Canceled',
};

/* Phase 3 (task #100) — Sentinel that DiscoveryActivitiesImpl.markRunCanceled
 * stamps into discovery_scan_runs.fatalErrorMessage when a destructive Stop
 * signal lands. The drawer detects this prefix and renders "Canceled by
 * user" instead of "Failed: CANCELED_BY_USER:…", and treats the run as
 * terminal-canceled rather than terminal-failed (different tone, different
 * minimised-pill copy). */
const CANCEL_SENTINEL_PREFIX = 'CANCELED_BY_USER:';
function isCanceledRun(run) {
  return !!(run && run.fatalErrorMessage && String(run.fatalErrorMessage).startsWith(CANCEL_SENTINEL_PREFIX));
}

const STATE_GLYPH = {
  QUEUED:    { symbol: '○', className: 'text-v2-ink-muted' },
  RUNNING:   { symbol: '⟳', className: 'text-v2-cyan animate-spin-slow' },
  COMPLETED: { symbol: '✓', className: 'text-v2-success' },
  /* The engine finished, but at least one of its rules never reached its
   * data source. A green tick beside "0 agents" would read as an
   * all-clear, and that is only true when every rule actually looked. */
  PARTIAL:   { symbol: '!', className: 'text-v2-warning' },
  FAILED:    { symbol: '✗', className: 'text-v2-danger' },
};

function formatElapsed(ms) {
  if (ms == null) return '—';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function countEnginesByState(engines, state) {
  return Object.values(engines || {}).filter((e) => e.state === state).length;
}

export default function ScanProgressDrawer({ runId, onClose, onComplete }) {
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [minimized, setMinimized] = useState(false);

  /* Phase 3 (task #100) — optimistic local pause state. The BE workflow
   * IS the source of truth (paused flag lives in DiscoveryScanWorkflowImpl)
   * but the observability row that this drawer polls has no "paused"
   * column today; surfacing pause state via a workflow @QueryMethod
   * would require a second REST round-trip per poll. Instead the FE
   * mirrors the operator's click locally — if the pause signal is
   * dropped (workflow already terminal), Temporal's WorkflowNotFound
   * surfaces a 404 and the drawer rolls back to "running". The
   * underlying scan continues regardless. */
  const [pausedLocally, setPausedLocally] = useState(false);

  /* Phase 3 (task #100) — inline destructive-Stop confirm state. First
   * click on Stop flips this to true and rerenders the button as
   * "Confirm stop" in danger tone; second click within
   * STOP_CONFIRM_WINDOW_MS actually signals cancelScan(). Clicking
   * elsewhere or letting the timer elapse rolls back to the safe
   * "Stop" button. V2 mandate — no modals. */
  const [stopConfirming, setStopConfirming] = useState(false);
  const [signalInflight, setSignalInflight] = useState(null); // 'pause' | 'resume' | 'stop' | null
  const [signalError, setSignalError] = useState(null);

  // Live "elapsed" timer driven by setInterval rather than waiting for
  // the next poll — the user sees the clock tick every second even
  // between API calls.
  const [tickMs, setTickMs] = useState(Date.now());
  useEffect(() => {
    if (!runId) return undefined;
    const interval = setInterval(() => setTickMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [runId]);

  // Polling loop. Stops as soon as we reach a terminal phase or the
  // component is unmounted / runId cleared.
  const completedHandledRef = useRef(false);
  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    completedHandledRef.current = false;

    const poll = async () => {
      try {
        const raw = await api.shadowDiscoveryScanRun(runId);
        if (cancelled) return;
        // apiFetch wraps successful JSON responses as { ok: true, data,
        // etag } and failed ones as { ok: false, error }. Without this
        // unwrap the drawer reads run.phase as undefined and freezes
        // on "Starting… · 0/0 engines · 00:00" while the backend is
        // actually running the scan fine.
        if (raw && raw.ok === false) {
          setError(raw.error || 'Unable to load scan run');
          setTimeout(() => { if (!cancelled) poll(); }, 3000);
          return;
        }
        const next = raw?.data ? raw.data : raw;
        setRun(next);
        setError(null);
        const terminal = next?.phase === 'COMPLETED' || next?.phase === 'FAILED';
        if (terminal) {
          if (!completedHandledRef.current) {
            completedHandledRef.current = true;
            if (typeof onComplete === 'function') onComplete(next);
            // Auto-collapse to pill 5 s after a clean completion so
            // the operator can keep working. Failures stay expanded.
            if (next.phase === 'COMPLETED') {
              setTimeout(() => { if (!cancelled) setMinimized(true); }, 5000);
            }
          }
          return; // don't reschedule
        }
        setTimeout(() => { if (!cancelled) poll(); }, 1500);
      } catch (e) {
        if (cancelled) return;
        // 404 most likely means the runId was evicted from the tracker.
        // We surface a friendly message but keep the drawer in place so
        // the operator can dismiss it deliberately.
        setError(e?.message || String(e));
        setTimeout(() => { if (!cancelled) poll(); }, 3000);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [runId, onComplete]);

  // DEF-DISCOVERY-DRAWER-NEG-TIME-001 — use the server-computed
  // elapsedMs as the anchor and add the client-side delta since the
  // last poll for tick smoothness. Parsing run.startedAt directly with
  // `new Date(...)` was buggy because the BE serializes LocalDateTime
  // without a 'Z' suffix; JS Date treats that as LOCAL time, while
  // tickMs is also local — but the underlying value is actually UTC,
  // so the subtraction came back negative by the timezone offset
  // ("-240:-58" for the EDT-4h tester). The elapsedMs field is
  // computed in the JVM via Duration.between(startedAt, now) so it's
  // timezone-agnostic by construction.
  //
  // IMPORTANT — keep this useState+useEffect ABOVE the
  // `if (!runId) return null;` early return below. React's rules-of-
  // hooks require the same number of hook calls on every render; the
  // first render (runId=null) and subsequent renders (runId set) must
  // call the same hooks in the same order or React throws error #310.
  const [pollAnchor, setPollAnchor] = useState({ at: Date.now(), elapsedMs: 0 });
  useEffect(() => {
    if (run?.elapsedMs == null) return;
    setPollAnchor({ at: Date.now(), elapsedMs: run.elapsedMs });
  }, [run?.elapsedMs]);

  /* Phase 3 (task #100) — destructive-Stop confirm rollback timer.
   * STOP_CONFIRM_WINDOW_MS is short enough that a stray click anywhere
   * else (which doesn't fire this handler) will outlast the window
   * and let the button revert. Pulling the window above the early
   * return keeps the React hook order stable per the rules of hooks. */
  const STOP_CONFIRM_WINDOW_MS = 4000;
  useEffect(() => {
    if (!stopConfirming) return undefined;
    const t = setTimeout(() => setStopConfirming(false), STOP_CONFIRM_WINDOW_MS);
    return () => clearTimeout(t);
  }, [stopConfirming]);

  /* Phase 3 (task #100) — signal dispatch. Each helper sets the
   * inflight tag (so the button shows a spinner / disabled state),
   * fires the BE endpoint, then clears inflight. Errors are surfaced
   * as a small toast-like banner under the controls — NOT the
   * polling-error path, which is reserved for transient network
   * failures on the GET endpoint. */
  const handlePause = useCallback(async () => {
    if (!runId || signalInflight) return;
    setSignalInflight('pause'); setSignalError(null);
    try {
      const r = await api.shadowDiscoveryScanPause(runId);
      if (r && r.ok === false) throw new Error(r.error || 'Pause failed');
      setPausedLocally(true);
    } catch (e) {
      setSignalError(`Pause failed: ${e?.message || String(e)}`);
    } finally { setSignalInflight(null); }
  }, [runId, signalInflight]);

  const handleResume = useCallback(async () => {
    if (!runId || signalInflight) return;
    setSignalInflight('resume'); setSignalError(null);
    try {
      const r = await api.shadowDiscoveryScanResume(runId);
      if (r && r.ok === false) throw new Error(r.error || 'Resume failed');
      setPausedLocally(false);
    } catch (e) {
      setSignalError(`Resume failed: ${e?.message || String(e)}`);
    } finally { setSignalInflight(null); }
  }, [runId, signalInflight]);

  const handleStopRequest = useCallback(() => {
    /* First click: arm the confirm. Second click within the window
     * actually dispatches. */
    if (!stopConfirming) { setStopConfirming(true); return; }
    if (!runId || signalInflight) return;
    setStopConfirming(false);
    setSignalInflight('stop'); setSignalError(null);
    (async () => {
      try {
        const r = await api.shadowDiscoveryScanStop(runId);
        if (r && r.ok === false) throw new Error(r.error || 'Stop failed');
        /* Don't optimistically flip phase to CANCELED — the workflow
         * still needs to unwind any in-flight rule activity, write
         * the observability row, etc. The next poll will surface the
         * CANCEL_SENTINEL fatalErrorMessage and the drawer will
         * render "Canceled by user". */
      } catch (e) {
        setSignalError(`Stop failed: ${e?.message || String(e)}`);
      } finally { setSignalInflight(null); }
    })();
  }, [runId, signalInflight, stopConfirming]);

  if (!runId) return null;

  const engines = run?.engines || {};
  const engineCount = Object.keys(engines).length;
  const completedCount =
    countEnginesByState(engines, 'COMPLETED')
    + countEnginesByState(engines, 'PARTIAL')
    + countEnginesByState(engines, 'FAILED');
  const canceled = isCanceledRun(run);
  /* Treat a CANCEL_SENTINEL-stamped FAILED phase as CANCELED for
   * display purposes. The BE schema doesn't have a dedicated CANCELED
   * phase today (the activity stamps FAILED + the sentinel error). */
  const displayPhase = canceled ? 'CANCELED' : run?.phase;
  const phaseLabel = PHASE_LABEL[displayPhase] || run?.phase || 'Starting…';
  const isTerminal = run?.phase === 'COMPLETED' || run?.phase === 'FAILED';
  const nonTerminal = run?.phase && !isTerminal;

  const elapsedMs = (run?.completedAt && run?.elapsedMs != null)
    ? run.elapsedMs
    : Math.max(0, pollAnchor.elapsedMs + (tickMs - pollAnchor.at));

  // ─────────── Minimised pill ───────────
  if (minimized) {
    const toneBg = canceled
      ? 'bg-v2-ink-muted/15 border-v2-ink-muted/40 text-v2-ink-secondary'
      : run?.phase === 'FAILED'
        ? 'bg-v2-danger/15 border-v2-danger/40 text-v2-danger'
        : run?.phase === 'COMPLETED'
          ? 'bg-v2-success/15 border-v2-success/40 text-v2-success'
          : pausedLocally
            ? 'bg-v2-warning/15 border-v2-warning/40 text-v2-warning'
            : 'bg-v2-cyan/15 border-v2-cyan/40 text-v2-cyan';
    const glyph = canceled ? '⊘'
      : run?.phase === 'FAILED' ? '✗'
        : run?.phase === 'COMPLETED' ? '✓'
          : pausedLocally ? '❚❚' : '⟳';
    const label = canceled
      ? 'Scan canceled'
      : run?.phase === 'COMPLETED'
        ? `Scan complete · ${run?.totalAgentsDiscovered || 0} agents`
        : run?.phase === 'FAILED'
          ? 'Scan failed'
          : pausedLocally
            ? `Scan paused · ${completedCount}/${engineCount}`
            : `Scan in progress · ${completedCount}/${engineCount}`;
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        aria-label="Expand scan progress"
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full border ${toneBg} shadow-v2-card text-[12px] font-medium hover:opacity-90`}
      >
        <span className={run?.phase === 'SCANNING' || run?.phase === 'CORRELATING' || run?.phase === 'FINALIZING' || run?.phase === 'QUEUED' ? 'animate-spin-slow inline-block' : 'inline-block'}>{glyph}</span>
        <span>{label}</span>
        <span className="font-mono-v2 text-[11px] opacity-80">{formatElapsed(elapsedMs)}</span>
      </button>
    );
  }

  // ─────────── Expanded drawer ───────────
  return (
    <>
      {/* Backdrop is intentionally absent — operator should keep working
          while the scan runs. The drawer floats above the page without
          blocking interaction with the underlying Hub. */}
      <aside
        role="dialog"
        aria-label="Discovery scan progress"
        className="fixed top-0 right-0 h-full bg-v2-surface border-l border-v2-border shadow-v2-drawer z-50 flex flex-col"
        style={{ width: PANEL_WIDTH }}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-v2-border">
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold m-0 truncate flex items-center gap-2">
              Discovery scan
              {pausedLocally && nonTerminal && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-v2-warning/15 text-v2-warning border border-v2-warning/30 font-medium">Paused</span>
              )}
              {canceled && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-v2-ink-muted/15 text-v2-ink-secondary border border-v2-ink-muted/30 font-medium">Canceled</span>
              )}
            </h2>
            <div className="text-[12px] text-v2-ink-secondary mt-0.5">
              {phaseLabel}
              <span className="mx-2 text-v2-ink-muted">·</span>
              {completedCount}/{engineCount} engines
              <span className="mx-2 text-v2-ink-muted">·</span>
              <span className="font-mono-v2">{formatElapsed(elapsedMs)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setMinimized(true)}
              aria-label="Minimise scan progress"
              title="Minimise"
              className="w-8 h-8 rounded hover:bg-v2-hover text-v2-ink-secondary flex items-center justify-center"
            >–</button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close scan progress"
              title="Close"
              className="w-8 h-8 rounded hover:bg-v2-hover text-v2-ink-secondary flex items-center justify-center"
            >×</button>
          </div>
        </header>

        {/* Phase 3 (task #100) — Pause / Resume / Stop controls. Visible
            only while the run is non-terminal (QUEUED / SCANNING /
            CORRELATING / FINALIZING). Stop uses inline two-click
            confirm — no modals per V2 mandate. */}
        {nonTerminal && (
          <div className="px-5 pt-3 pb-1">
            <div className="flex items-center gap-2">
              {pausedLocally ? (
                <button
                  type="button"
                  onClick={handleResume}
                  disabled={!!signalInflight}
                  className="px-3 py-1.5 rounded text-[12px] font-medium border border-v2-action bg-v2-action-soft text-v2-action hover:bg-v2-action/15 disabled:opacity-60"
                >
                  {signalInflight === 'resume' ? 'Resuming…' : '▶ Resume'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePause}
                  disabled={!!signalInflight}
                  className="px-3 py-1.5 rounded text-[12px] font-medium border border-v2-border-soft bg-v2-surface text-v2-ink hover:bg-v2-hover disabled:opacity-60"
                >
                  {signalInflight === 'pause' ? 'Pausing…' : '❚❚ Pause'}
                </button>
              )}
              <button
                type="button"
                onClick={handleStopRequest}
                disabled={!!signalInflight}
                className={
                  'px-3 py-1.5 rounded text-[12px] font-medium border transition-colors disabled:opacity-60 ' +
                  (stopConfirming
                    ? 'border-v2-danger bg-v2-danger text-white hover:bg-v2-danger/90'
                    : 'border-v2-border-soft bg-v2-surface text-v2-ink hover:bg-v2-hover')
                }
                aria-pressed={stopConfirming}
              >
                {signalInflight === 'stop'
                  ? 'Stopping…'
                  : stopConfirming
                    ? '⏹ Confirm stop · destructive'
                    : '⏹ Stop'}
              </button>
              {stopConfirming && (
                <span className="text-[11px] text-v2-ink-muted">
                  Click again to confirm — this skips remaining rules + engines.
                </span>
              )}
            </div>
            {signalError && (
              <div className="mt-2 text-[11px] text-v2-danger">{signalError}</div>
            )}
          </div>
        )}

        {/* Progress bar */}
        <div className="px-5 pt-3">
          <div className="h-1.5 w-full bg-v2-border-soft rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                run?.phase === 'FAILED' ? 'bg-v2-danger'
                  : run?.phase === 'COMPLETED' ? 'bg-v2-success' : 'bg-v2-cyan'
              }`}
              style={{ width: engineCount > 0 ? `${(completedCount / engineCount) * 100}%` : '0%' }}
            />
          </div>
        </div>

        {/* Engine rows */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {engineCount === 0 && (
            <div className="text-[12px] text-v2-ink-muted py-2">
              Initialising scan…
            </div>
          )}
          <ul className="space-y-2">
            {sortEngineEntries(engines).map(([engineType, state]) => (
              <EngineRow
                key={engineType}
                engineType={engineType}
                state={state}
                tickMs={tickMs}
                pollAnchor={pollAnchor}
              />
            ))}
          </ul>

          {/* Run-level notes */}
          {run?.notes && run.notes.length > 0 && (
            <div className="mt-4 pt-3 border-t border-v2-border-soft">
              <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted mb-2">Run notes</div>
              <ul className="space-y-1">
                {run.notes.map((note, idx) => (
                  <li key={idx} className="text-[12px] text-v2-ink-secondary">• {note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Fatal error — sentinel-aware. A CANCEL_SENTINEL prefix
              indicates the run was canceled by user via destructive Stop,
              not an actual engine/correlation failure; surface as a
              neutral "Canceled by user" panel with the timestamp instead
              of red "Fatal error". */}
          {run?.fatalErrorMessage && canceled && (
            <div className="mt-4 pt-3 border-t border-v2-border-soft">
              <div className="text-[11px] uppercase tracking-wider text-v2-ink-secondary mb-2">Canceled by user</div>
              <div className="text-[12px] text-v2-ink-secondary bg-v2-surface-2 border border-v2-border-soft rounded px-3 py-2 break-words">
                Destructive Stop signaled — workflow short-circuited remaining rules + engines.
                {run.completedAt && (
                  <span className="block text-[11px] text-v2-ink-muted mt-1 font-mono-v2">
                    at {new Date(run.completedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}
          {run?.fatalErrorMessage && !canceled && (
            <div className="mt-4 pt-3 border-t border-v2-border-soft">
              <div className="text-[11px] uppercase tracking-wider text-v2-danger mb-2">Fatal error</div>
              <div className="text-[12px] text-v2-ink font-mono-v2 bg-v2-danger/10 border border-v2-danger/30 rounded px-3 py-2 break-words">
                {run.fatalErrorMessage}
              </div>
            </div>
          )}

          {/* Polling error */}
          {error && (
            <div className="mt-4 pt-3 border-t border-v2-border-soft">
              <div className="text-[11px] uppercase tracking-wider text-v2-warning mb-2">Polling error</div>
              <div className="text-[12px] text-v2-ink-secondary">{error}</div>
            </div>
          )}
        </div>

        {/* Footer summary */}
        <footer className="px-5 py-3 border-t border-v2-border bg-v2-surface-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-center">
            <SummaryStat label="Agents" value={run?.totalAgentsDiscovered ?? '—'} />
            <SummaryStat label="Signals" value={run?.totalSignalsGenerated ?? '—'} />
            <SummaryStat
              label="Auto-enrolled"
              value={run?.autoPromotedByPolicy ?? '—'}
            />
          </div>
          {run?.correlatedSignalCount > 0 && (
            <div className="text-[11px] text-v2-ink-muted text-center mt-2">
              Correlated {run.correlatedSignalCount} signals across engines
            </div>
          )}
        </footer>
      </aside>
    </>
  );
}

function EngineRow({ engineType, state, tickMs, pollAnchor }) {
  const label = ENGINE_LABEL[engineType] || engineType;
  const glyph = STATE_GLYPH[state.state] || STATE_GLYPH.QUEUED;
  const isRunning = state.state === 'RUNNING';
  // DEF-DISCOVERY-DRAWER-NEG-TIME-001 — same timezone-anchored
  // calculation as the header. The BE's per-engine elapsedMs is
  // serialised on every poll (or null while the engine is still
  // QUEUED), so we use that as the baseline and add the client
  // delta for inter-poll smoothness on RUNNING rows. Once the
  // engine is COMPLETED/FAILED, state.elapsedMs is frozen and we
  // display it verbatim.
  let elapsedMs = null;
  if (state.completedAt && state.elapsedMs != null) {
    elapsedMs = state.elapsedMs;
  } else if (isRunning && state.elapsedMs != null) {
    elapsedMs = Math.max(0, state.elapsedMs + (tickMs - pollAnchor.at));
  }

  const rulesScanned = state.rulesScanned ?? 0;
  const rulesSkipped = state.rulesSkipped ?? 0;
  const rulesFailed = state.rulesFailed ?? 0;
  const rulesTotal = rulesScanned + rulesSkipped + rulesFailed;
  const isPartial = state.state === 'PARTIAL';

  const rowTone =
    state.state === 'FAILED'
      ? 'border-v2-danger/40 bg-v2-danger/5'
      : isPartial
        ? 'border-v2-warning/40 bg-v2-warning/5'
        : 'border-v2-border-soft';

  return (
    <li className={`rounded border ${rowTone} px-3 py-2`}>
      <div className="flex items-center gap-3">
        <span className={`w-5 text-[14px] leading-none ${glyph.className}`}>{glyph.symbol}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium truncate">{label}</div>
          <div className="text-[11px] text-v2-ink-muted flex items-center gap-2">
            <span className="font-mono-v2">{engineType}</span>
            {elapsedMs != null && (
              <>
                <span>·</span>
                <span className="font-mono-v2">{formatElapsed(elapsedMs)}</span>
              </>
            )}
          </div>
        </div>
        {(state.state === 'COMPLETED' || state.state === 'PARTIAL' || state.state === 'RUNNING') && (
          <div className="text-right text-[11px]">
            <div>
              <span className="text-v2-ink-muted">agents:</span>{' '}
              <span className="font-mono-v2 text-v2-ink">{state.agentsDiscovered ?? 0}</span>
            </div>
            <div>
              <span className="text-v2-ink-muted">signals:</span>{' '}
              <span className="font-mono-v2 text-v2-ink">{state.signalsGenerated ?? 0}</span>
            </div>
          </div>
        )}
      </div>

      {/* Coverage line. A count of findings only means something next to a
        * count of what was actually inspected — without it, "0 agents" from
        * an engine whose connector was never set up looks identical to a
        * clean estate. Hidden until at least one rule has reported, so a
        * queued or freshly started engine stays uncluttered. */}
      {rulesTotal > 0 && (
        <div className="mt-1.5 text-[11px]">
          <span className={isPartial || rulesFailed > 0 ? 'text-v2-warning' : 'text-v2-ink-muted'}>
            {rulesScanned} of {rulesTotal} {rulesTotal === 1 ? 'rule' : 'rules'} scanned
          </span>
          {rulesSkipped > 0 && (
            <span className="text-v2-ink-muted"> · {rulesSkipped} need setup</span>
          )}
          {rulesFailed > 0 && (
            <span className="text-v2-warning"> · {rulesFailed} did not finish</span>
          )}
        </div>
      )}
      {/* DISC-IMPR-A — Per-engine targets. The BE stamps the human-
       * readable targets the engine is about to scan (repo slugs, cloud
       * account IDs, CIDR ranges, MCP server hostnames) onto
       * EngineRunState before flipping to RUNNING. Render them under
       * the engine row so the operator can see at a glance which
       * targets are in scope for this scan. Cap visible rows at 5;
       * everything else is summarised. */}
      {Array.isArray(state.targets) && state.targets.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted mb-0.5">
            Targets · {state.targets.length}
          </div>
          <ul className="space-y-0.5">
            {state.targets.slice(0, 5).map((t, i) => (
              <li key={i} className="text-[11px] text-v2-ink-secondary font-mono-v2 truncate" title={t}>
                · {t}
              </li>
            ))}
            {state.targets.length > 5 && (
              <li className="text-[10.5px] text-v2-ink-muted italic">
                …and {state.targets.length - 5} more
              </li>
            )}
          </ul>
        </div>
      )}
      {state.errorMessage && (
        <div className="mt-2 text-[11px] text-v2-danger font-mono-v2 break-words">
          {state.errorMessage}
        </div>
      )}
      {/* Notes carry the per-rule guidance for anything that did not get
        * scanned, so the operator can close the gap. Capped at six: one
        * engine cannot have many more rules than that, and an unbounded
        * list would push the summary out of view. */}
      {state.notes && state.notes.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {state.notes.slice(-6).map((n, i) => (
            <li key={i} className="text-[11px] text-v2-ink-secondary">↳ {n}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div>
      <div className="text-[18px] font-semibold tabular-nums leading-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-v2-ink-muted mt-0.5">{label}</div>
    </div>
  );
}
